import { StateCreator } from "zustand";
import { supabase } from "@/lib/supabase";
import { showNotification } from "../Central/selectors";
import { Logger } from "@/lib/logger/Logger";
import { JobStoreUnion } from ".";
import { produce } from "immer";
import posthog from "posthog-js";
import {
	Delivery,
	CourierDelivery,
	CreateDeliveryParams,
	DeliveryStatusEnum,
} from "@/lib/types/delivery.types";

interface DeliveryState {
	deliveries: Record<number, Delivery>;
	deliveriesLoading: boolean;
	courierDeliveries: CourierDelivery[];
	courierDeliveriesLoading: boolean;
}

export const initialDeliveryState: DeliveryState = {
	deliveries: {},
	deliveriesLoading: false,
	courierDeliveries: [],
	courierDeliveriesLoading: false,
};

export interface DeliverySlice extends DeliveryState {
	fetchDeliveries: (jobId: number) => Promise<void>;
	createDelivery: (params: CreateDeliveryParams) => Promise<number | null>;
	updateDelivery: (
		deliveryId: number,
		fields: Partial<Delivery>
	) => Promise<void>;
	updateDeliveryStatus: (
		deliveryId: number,
		status: DeliveryStatusEnum
	) => Promise<void>;
	deleteDelivery: (deliveryId: number) => Promise<void>;
	fetchCourierDeliveries: () => Promise<void>;
	resetDeliveries: () => void;
	handleDeliveryRealtimeEvent: (payload: any) => void;
}

export const createDeliveryStore: StateCreator<
	JobStoreUnion,
	[],
	[],
	DeliverySlice
> = (set, get) => ({
	...initialDeliveryState,

	fetchDeliveries: async (jobId: number) => {
		if (!jobId) {
			Logger.error("fetchDeliveries called with null/undefined jobId");
			return;
		}

		set({ deliveriesLoading: true });

		const { data, error } = await supabase
			.from("deliveries")
			.select("*")
			.eq("job_id", jobId)
			.order("created_at", { ascending: false });

		if (error) {
			Logger.error(error, {}, "Error fetching deliveries");
			showNotification({
				message: "Fehler beim Abrufen der Lieferungen",
				type: "error",
			});
			set({ deliveriesLoading: false });
			return;
		}

		const lookup: Record<number, Delivery> = {};
		(data as Delivery[]).forEach((d: Delivery) => {
			lookup[d.id] = d;
		});

		set({
			deliveries: lookup,
			deliveriesLoading: false,
		});
	},

	createDelivery: async (params: CreateDeliveryParams) => {
		const { data, error } = await supabase.rpc("create_delivery", {
			p_job_id: params.job_id,
			p_courier_type: params.courier_type,
			p_profile_id: params.profile_id ?? undefined,
			p_courier_company_name: params.courier_company_name ?? undefined,
			p_description: params.description ?? undefined,
			p_scheduled_departure_at:
				params.scheduled_departure_at ?? undefined,
			p_is_final_delivery: params.is_final_delivery ?? false,
		});

		if (error) {
			Logger.error(error, {}, "Error creating delivery");
			showNotification({
				message: "Fehler beim Erstellen der Lieferung",
				type: "error",
			});
			return null;
		}

		showNotification({
			message: "Lieferung erstellt",
			type: "success",
		});

		posthog.capture("delivery_created", {
			courier_type: params.courier_type,
			has_description: !!params.description,
			has_scheduled_departure: !!params.scheduled_departure_at,
			is_final_delivery: params.is_final_delivery ?? false,
		});

		if (params.is_final_delivery && params.scheduled_departure_at) {
			const dep = new Date(params.scheduled_departure_at);
			// sv-SE locale outputs ISO format: YYYY-MM-DD / HH:MM:SS
			const dateStr = dep.toLocaleDateString("sv-SE");
			const timeStr = dep.toLocaleTimeString("sv-SE");
			await get().updateJobFields({
				outgoing_order_date: dateStr,
				outgoing_order_time: timeStr,
			});
		}

		await get().fetchDeliveries(params.job_id);
		return data as number;
	},

	updateDelivery: async (deliveryId: number, fields: Partial<Delivery>) => {
		const previous = get().deliveries[deliveryId];
		if (!previous) return;

		set(
			produce((state) => {
				state.deliveries[deliveryId] = {
					...state.deliveries[deliveryId],
					...fields,
				};
			})
		);

		const { error } = await supabase
			.from("deliveries")
			.update(fields as any)
			.eq("id", deliveryId);

		if (error) {
			Logger.error(error, {}, "Error updating delivery");
			showNotification({
				message: "Fehler beim Aktualisieren der Lieferung",
				type: "error",
			});
			set(
				produce((state) => {
					state.deliveries[deliveryId] = previous;
				})
			);
			return;
		}

		showNotification({
			message: "Lieferung aktualisiert",
			type: "success",
		});
	},

	updateDeliveryStatus: async (
		deliveryId: number,
		status: DeliveryStatusEnum
	) => {
		const { error } = await supabase.rpc("update_delivery_status", {
			p_delivery_id: deliveryId,
			p_status: status,
		});

		if (error) {
			Logger.error(error, {}, "Error updating delivery status");
			showNotification({
				message: "Fehler beim Aktualisieren des Lieferungsstatus",
				type: "error",
			});
			return;
		}

		set(
			produce((state) => {
				if (state.deliveries[deliveryId]) {
					state.deliveries[deliveryId].status = status;
					if (status === DeliveryStatusEnum.DELIVERED) {
						state.deliveries[deliveryId].delivered_at =
							new Date().toISOString();
					}
				}
			})
		);

		posthog.capture("delivery_status_changed", {
			new_status: status,
		});

		showNotification({
			message: "Lieferungsstatus aktualisiert",
			type: "success",
		});
	},

	deleteDelivery: async (deliveryId: number) => {
		if (!deliveryId) {
			Logger.error(
				"deleteDelivery called with null/undefined deliveryId"
			);
			return;
		}

		const previous = get().deliveries[deliveryId];

		set(
			produce((state) => {
				delete state.deliveries[deliveryId];
			})
		);

		const { error } = await supabase
			.from("deliveries")
			.delete()
			.eq("id", deliveryId);

		if (error) {
			Logger.error(error, {}, "Error deleting delivery");
			showNotification({
				message: "Fehler beim Löschen der Lieferung",
				type: "error",
			});
			if (previous) {
				set(
					produce((state) => {
						state.deliveries[deliveryId] = previous;
					})
				);
			}
			return;
		}

		posthog.capture("delivery_deleted");

		showNotification({
			message: "Lieferung gelöscht",
			type: "success",
		});
	},

	fetchCourierDeliveries: async () => {
		set({ courierDeliveriesLoading: true });

		const { data, error } = await supabase.rpc("get_courier_deliveries");

		if (error) {
			Logger.error(error, {}, "Error fetching courier deliveries");
			showNotification({
				message: "Fehler beim Abrufen der Lieferungen",
				type: "error",
			});
			set({ courierDeliveriesLoading: false });
			return;
		}

		set({
			courierDeliveries: (data || []) as CourierDelivery[],
			courierDeliveriesLoading: false,
		});
	},

	resetDeliveries: () => {
		set(initialDeliveryState);
	},

	handleDeliveryRealtimeEvent: (payload: any) => {
		const { eventType, new: newRecord, old: oldRecord } = payload;
		const currentJobId = get().job?.id;

		if (eventType === "INSERT" && newRecord?.job_id === currentJobId) {
			set(
				produce((state) => {
					state.deliveries[newRecord.id] = newRecord;
				})
			);
		} else if (
			eventType === "UPDATE" &&
			newRecord?.job_id === currentJobId
		) {
			set(
				produce((state) => {
					state.deliveries[newRecord.id] = newRecord;
				})
			);
		} else if (eventType === "DELETE" && oldRecord?.id) {
			set(
				produce((state) => {
					delete state.deliveries[oldRecord.id];
				})
			);
		}
	},
});
